import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PasswordSetForm } from '../components/PasswordSetForm';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as portalPasswordService from '../services/portalPasswordService';

/**
 * Authenticated change-password page used from Account → "Change Password".
 * Reuses `PasswordSetForm` so the rules and validation match the
 * forgot-password / set-initial flows. The "current password" field is
 * required because login verifies it before rotating the new one.
 */
export function ChangePasswordPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token } = usePortalAuth();
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(false);

	const handleSubmit = async (values: {
		currentPassword: string;
		newPassword: string;
	}): Promise<void> => {
		if (token == null) {
			setSubmitError('Your session has expired. Please sign in again.');
			return;
		}
		setSubmitting(true);
		setSubmitError(null);
		try {
			await portalPasswordService.changePassword(
				token,
				values.currentPassword,
				values.newPassword,
			);
			setDone(true);
		} catch (error) {
			const message =
				error instanceof Error ? error.message : 'Unable to change password';
			setSubmitError(message);
		} finally {
			setSubmitting(false);
		}
	};

	if (done) {
		return (
			<div className="px-4 py-8 max-w-md mx-auto">
				<div className="bg-brand-surface border border-brand-border rounded-xl p-6 space-y-4">
					<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
						{t('Password updated')}
					</h1>
					<p className="text-sm text-gray-600 dark:text-gray-400">
						{t('Your password has been changed.')}
					</p>
					<button
						type="button"
						onClick={() => navigate('/account')}
						className="inline-block rounded-md bg-primary-600 hover:bg-primary-700 text-white font-medium px-4 py-2 transition-colors"
					>
						{t('Back to account')}
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="px-4 py-8 max-w-md mx-auto">
			<div className="bg-brand-surface border border-brand-border rounded-xl p-6">
				<PasswordSetForm
					title="Change password"
					subtitle="Enter your current password, then choose a new one."
					requireCurrentPassword
					submitLabel="Update password"
					onSubmit={handleSubmit}
					errorMessage={submitError}
					isSubmitting={submitting}
				/>
			</div>
		</div>
	);
}
